"use client";

import React, { useState, useRef, useEffect } from "react";
import { Copy, Eye, EyeOff, RefreshCw, Trash2, Plus, Calendar, X } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/toast-provider";

interface ApiKey {
  id: string;
  name: string;
  key: string;
  createdAt: Date;
  expiresAt: Date | null;
}

export default function ApiKeyManager() {
  const { addToast } = useToast();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([
    {
      id: "1",
      name: "Production API Key",
      key: "pk_live_51NxrTyLmCgABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890",
      createdAt: new Date("2023-09-15"),
      expiresAt: new Date("2024-09-15"),
    },
    {
      id: "2",
      name: "Development API Key",
      key: "pk_test_51NxrTyLmCgABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890",
      createdAt: new Date("2023-08-01"),
      expiresAt: null,
    },
  ]);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyExpiration, setNewKeyExpiration] = useState("");
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const toggleKeyVisibility = (id: string) => {
    setVisibleKeys((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    
    addToast({
      title: "Copied to clipboard",
      description: "API key has been copied to your clipboard",
      type: "success",
      duration: 2000
    });
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  const maskApiKey = (key: string) => {
    return `${key.substring(0, 8)}...${key.substring(key.length - 4)}`;
  };

  const handleCreateKey = () => {
    if (!newKeyName.trim()) return;

    const newKey = {
      id: Math.random().toString(36).substring(2, 9),
      name: newKeyName,
      key: `pk_${Math.random().toString(36).substring(2, 15)}_${Math.random().toString(36).substring(2, 15)}`,
      createdAt: new Date(),
      expiresAt: newKeyExpiration ? new Date(newKeyExpiration) : null,
    };

    setApiKeys((prev) => [...prev, newKey]);
    setNewlyCreatedKey(newKey.key);
    setNewKeyName("");
    setNewKeyExpiration("");
    setShowCreateForm(false);
    
    addToast({
      title: "API Key Created",
      description: "Your new API key has been created successfully",
      type: "success",
      duration: 3000
    });
  };

  const regenerateKey = (id: string) => {
    setApiKeys((prev) =>
      prev.map((key) => {
        if (key.id === id) {
          const newKey = `pk_${Math.random().toString(36).substring(2, 15)}_${Math.random().toString(36).substring(2, 15)}`;
          setNewlyCreatedKey(newKey);
          return { ...key, key: newKey, createdAt: new Date() };
        }
        return key;
      })
    );
    
    addToast({
      title: "API Key Regenerated",
      description: "Your API key has been regenerated successfully",
      type: "success",
      duration: 3000
    });
  };

  const revokeKey = (id: string) => {
    setApiKeys((prev) => prev.filter((key) => key.id !== id));
    
    addToast({
      title: "API Key Revoked",
      description: "Your API key has been revoked",
      type: "info",
      duration: 3000
    });
  };

  const closeNewKeyAlert = () => {
    setNewlyCreatedKey(null);
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">API Keys</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 h-10 px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
        >
          <Plus size={18} />
          <span>Create New API Key</span>
        </button>
      </div>

      {newlyCreatedKey && (
        <div className="relative mt-4 p-4 border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 rounded-lg shadow-sm transition-all duration-300 animate-fadeIn">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-medium text-green-800 dark:text-green-400">New API Key Created</h3>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                Make sure to copy your API key now. You won't be able to see it again!
              </p>
              <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-green-300 dark:border-green-700 font-mono text-sm break-all">
                {newlyCreatedKey}
              </div>
            </div>
            <button
              onClick={closeNewKeyAlert}
              className="text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 transition-colors duration-200"
            >
              <X size={18} />
            </button>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => copyToClipboard(newlyCreatedKey, "new")}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 transition-colors duration-200"
            >
              <Copy size={14} />
              {copiedKey === "new" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {showCreateForm && (
        <div className="mt-6 p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-300 animate-fadeIn">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Create New API Key</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="keyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Key Name
              </label>
              <input
                type="text"
                id="keyName"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g. Production API Key"
                className="flex h-9 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm shadow-black/5 transition-shadow placeholder:text-muted-foreground/70 focus-visible:border-indigo-500 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div>
              <label htmlFor="keyExpiration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Expiration Date (Optional)
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="keyExpiration"
                  value={newKeyExpiration}
                  onChange={(e) => setNewKeyExpiration(e.target.value)}
                  className="flex h-9 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm shadow-black/5 transition-shadow placeholder:text-muted-foreground/70 focus-visible:border-indigo-500 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-50 pl-9"
                />
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-gray-400">
                  <Calendar size={16} />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateKey}
                disabled={!newKeyName.trim()}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create API Key
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4">
        {apiKeys.map((apiKey) => (
          <div
            key={apiKey.id}
            className="group p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{apiKey.name}</h3>
                <div className="mt-2 flex items-center">
                  <div className="font-mono text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-md">
                    {visibleKeys[apiKey.id] ? apiKey.key : maskApiKey(apiKey.key)}
                  </div>
                  <button
                    onClick={() => toggleKeyVisibility(apiKey.id)}
                    className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200"
                    aria-label={visibleKeys[apiKey.id] ? "Hide API key" : "Show API key"}
                  >
                    {visibleKeys[apiKey.id] ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  <button
                    onClick={() => copyToClipboard(apiKey.key, apiKey.id)}
                    className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200"
                    aria-label="Copy API key"
                  >
                    <Copy size={18} />
                    {copiedKey === apiKey.id && (
                      <span className="absolute ml-2 text-xs text-green-600 dark:text-green-400">Copied!</span>
                    )}
                  </button>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => regenerateKey(apiKey.id)}
                  className="p-2 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                  aria-label="Regenerate API key"
                >
                  <RefreshCw size={18} />
                </button>
                <button
                  onClick={() => revokeKey(apiKey.id)}
                  className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                  aria-label="Revoke API key"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm text-gray-500 dark:text-gray-400">
              <span>Created: {format(apiKey.createdAt, "MMM d, yyyy")}</span>
              {apiKey.expiresAt && (
                <span className="ml-4">
                  Expires: {format(apiKey.expiresAt, "MMM d, yyyy")}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {apiKeys.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">No API keys found. Create your first API key to get started.</p>
        </div>
      )}
    </div>
  );
}
